import { Alert, Box, Paper } from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { Reservation } from "shared/reservation"
import { ReservationThingTypeMrn } from "shared/reservationData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import type { SstDoor, SstLocker, LockerGrid } from "shared/sst"
import type { Sst2Door, Sst2Locker } from "shared/sst2"
import { useAppSelector } from "src/app/hooks"
import { sl2ApiUrl } from "src/features/baseQuerySl2"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectThingsByThingTypeMrnFilter } from "src/features/things/thingsSlice"
import { orderedV2Modules } from "../lockerModuleOrder"
import { OsLockerDoorV2 } from "../OsLockerDoorV2"
import { lockerDimRatio, V1Module, V2Module } from "./SaLockerModules"
import {
  inspectDoor,
  inspectLockerReservations,
  isDoorOccupied,
  reservationVersionOf,
  sstDoorKeysOf,
} from "./saLockerDoorInspection"
import SaLockerDoorInspector from "./SaLockerDoorInspector"
import SaLockerObjectViewer from "./SaLockerObjectViewer"
import SaLockerUnitHeader from "./SaLockerUnitHeader"
import {
  reservationsReadProvenance,
  snapshotProvenance,
} from "./saLockerProvenance"

/**
 * Read-only twin of the operator locker dashboards, for the SA locker page.
 *
 * Renders the SAME physical layout an operator sees — real module shells, real
 * door tiles in their real cell positions — so a super-admin can recognise the
 * machine and match what support is describing on the phone. What they drop are
 * the MUTATING affordances: no reservation actions, no deposit report, no door
 * commands. Clicking a door opens an inspection drawer instead, showing the
 * partner payload and the SL2 Reservation Thing side by side.
 *
 * It deliberately REUSES `OsLockerDoor` / `OsLockerDoorV2` rather than
 * reimplementing the tiles: door colour semantics (reserved, expiring, paid,
 * deposited) are subtle and would silently drift out of sync in a copy. Both
 * tiles derive their state from data already on the locker snapshot — V1 from
 * `door.links`, V2 from the embedded reservations — so neither needs the OS
 * reservation store this page does not load.
 *
 * ## LOCKER-355 — one matcher, one occupancy predicate
 *
 * Two expressions in this file used to duplicate logic that lives in
 * `saLockerDoorInspection.ts`, and both had drifted from it:
 *
 *  - `reservationForV1` carried its own inline `sstReservation.door.door_id`
 *    read — the same key that is absent on every partner-sourced v1 row — so v1
 *    tiles never coloured from their reservation and instead rendered "busy".
 *  - the occupancy count was a third, independent expression, so the percentage
 *    in the header and the tiles below it could disagree with nothing to catch
 *    it.
 *
 * Both now call the shared exports, which is what makes the DoD's
 * "occupancy agrees with the doors rendered occupied" checkable rather than
 * merely true today.
 */

// ── Dashboard ─────────────────────────────────────────────

/** The `ap.limit` ceiling, named so the provenance line can print it. */
const RESERVATION_READ_LIMIT = 2000

export const SaLockerDashboardView: FC<{
  locker: Locker
  name: string
  onRefresh: () => void
  isRefreshing?: boolean
}> = ({ locker, onRefresh, isRefreshing }) => {
  const { t } = useTranslation()
  const apiVersion = locker.sstApiVersion === "v2" ? "v2" : "v1"
  const isV2 = apiVersion === "v2"
  const [selectedDoor, setSelectedDoor] = useState<SstDoor | Sst2Door | null>(null)
  const [objectViewerOpen, setObjectViewerOpen] = useState(false)

  // SL2 Reservations for this locker. Super-admins pass `enforceTenantAccess`,
  // so the OS access pattern is reusable here with the locker's own tenantId
  // rather than the (absent) selected tenant.
  useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetLockerCurrentReservations,
        "ap.tenantId": locker.tenantId,
        "ap.lockerId": locker.id,
        "ap.limit": RESERVATION_READ_LIMIT,
        tenantId: locker.tenantId,
      },
    },
    { skip: !locker.tenantId || !locker.id },
  )

  const reservationFilter = useCallback(
    (thing: Reservation) => thing.lockerId === locker.id,
    [locker.id],
  )
  const reservations = useAppSelector(
    (st) =>
      selectThingsByThingTypeMrnFilter(st, ReservationThingTypeMrn, reservationFilter),
    (a, b) => JSON.stringify(a) === JSON.stringify(b),
  ) as unknown as Reservation[]

  const sst2Locker = locker.sst2Locker as Sst2Locker | undefined
  const sstLocker = locker.sstLocker as SstLocker | undefined

  const v2Doors = useMemo(
    () => [...(sst2Locker?.doors || [])].sort((a, b) => a.door_number - b.door_number),
    [sst2Locker?.doors],
  )
  // V2 templates, reordered by the locker's moduleMapping exactly as the OS
  // dashboard does (LOCKER-390) — same reason `v1Grids` below does it: this
  // view exists to show the SAME module letters as the signage on the physical
  // unit, so it cannot order modules differently from OsLockerDashboardV2.
  const v2Templates = useMemo(
    () => orderedV2Modules(sst2Locker, locker.moduleMapping),
    [sst2Locker, locker.moduleMapping],
  )

  /** Every door of the version this locker is on, in one list. */
  const doors: (SstDoor | Sst2Door)[] = useMemo(
    () => (isV2 ? v2Doors : sstLocker?.doors || []),
    [isV2, v2Doors, sstLocker?.doors],
  )

  const inspection = useMemo(
    () =>
      selectedDoor
        ? inspectDoor({ apiVersion, door: selectedDoor, reservations })
        : undefined,
    [selectedDoor, apiVersion, reservations],
  )

  /**
   * What could not be placed on any door — the answer an empty door owes the
   * admin looking at it. Computed once for the locker rather than per door,
   * because an unplaceable row belongs to no door by construction.
   */
  const pairing = useMemo(
    () => inspectLockerReservations({ apiVersion, doors, reservations }),
    [apiVersion, doors, reservations],
  )

  /**
   * V1 tiles need their matched Reservation to colour themselves correctly.
   * Same matcher `inspectDoor` uses — a second inline expression here is what
   * LOCKER-355 was, twice over.
   */
  const reservationForV1 = useCallback(
    (door: SstDoor) =>
      reservations.find(
        (r) =>
          reservationVersionOf(r, apiVersion) === "v1" &&
          sstDoorKeysOf(r).includes(door.door_id),
      ),
    [reservations, apiVersion],
  )

  // V1 grids, reordered by the locker's moduleMapping exactly as the OS
  // dashboard does — otherwise the SA view labels modules differently from
  // the signage on the physical unit.
  const v1Grids = useMemo(() => {
    const raw = (sstLocker?.locker_grid || []).map((g, i) => ({ ...g, name: `${g?.name}-${i}` }))
    const mapping = locker.moduleMapping
    if (!mapping?.length) return raw
    return mapping.map((m) => raw[m.sl2Idx]).filter(Boolean)
  }, [sstLocker?.locker_grid, locker.moduleMapping])

  const isOnline = isV2 ? !!sst2Locker?.is_online : !!sstLocker?.is_online
  const hasSnapshot = isV2 ? !!sst2Locker : !!sstLocker

  const occupiedDoors = useMemo(
    () => doors.filter((door) => isDoorOccupied({ apiVersion, door })).length,
    [doors, apiVersion],
  )

  const provenance = useMemo(
    () => [
      reservationsReadProvenance({
        base: sl2ApiUrl,
        tenantId: locker.tenantId,
        lockerId: locker.id,
        limit: RESERVATION_READ_LIMIT,
      }),
      snapshotProvenance({
        base: sl2ApiUrl,
        apiVersion,
        tenantId: locker.tenantId,
      }),
    ],
    [locker.tenantId, locker.id, apiVersion],
  )

  return (
    <Box>
      <SaLockerUnitHeader
        apiVersion={apiVersion}
        hasSnapshot={hasSnapshot}
        isOnline={isOnline}
        totalDoors={doors.length}
        occupiedDoors={occupiedDoors}
        versionMismatchedCount={pairing.versionMismatched.length}
        unpairableCount={pairing.unpairable.length}
        provenance={provenance}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        onOpenLockerObject={() => setObjectViewerOpen(true)}
      />

      {!hasSnapshot ? (
        <Alert severity="info" variant="outlined" sx={{ m: 1, fontSize: "0.8rem" }}>
          {t("saLockerConsole.noSnapshot")}
        </Alert>
      ) : (
        <Box sx={{ mt: 1, whiteSpace: "nowrap", overflowX: "auto" }}>
          {isV2 && v2Templates.length === 0 ? (
            // Same fallback the operator dashboard uses: a locker with no
            // template association still has doors, and rendering nothing would
            // read as "this locker is broken" rather than "it has no layout".
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 10, borderWidth: "5px", bgcolor: "grey.100" }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {v2Doors.map((door) => (
                  <Box
                    key={door.id}
                    data-testid={`sa-door-${door.id}`}
                    sx={{ width: `${300 * lockerDimRatio}px`, height: `${300 * lockerDimRatio}px` }}
                  >
                    <OsLockerDoorV2
                      door={door}
                      alwaysClickable
                      onClick={() => setSelectedDoor(door)}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : isV2 ? (
            v2Templates.map(({ tmpl, originalIndex }, i) => (
                <V2Module
                  key={tmpl.name || originalIndex}
                  template={tmpl}
                  doors={v2Doors.filter((d) => d.locker_group === originalIndex)}
                  label={`${t("module")} ${String.fromCharCode(65 + i)}`}
                  onDoorClick={setSelectedDoor}
                />
              ))
          ) : (
            v1Grids.map((grid, i) => (
                <V1Module
                  key={grid.name || i}
                  grid={grid as LockerGrid}
                  locker={locker}
                  doors={(sstLocker?.doors || []).filter((d) =>
                    ((grid as any).cells_group || []).some((c: any) => c?.door_id === d.door_id),
                  )}
                  label={`${t("module")} ${String.fromCharCode(65 + i)}`}
                  reservationFor={reservationForV1}
                  onDoorClick={setSelectedDoor}
                />
            ))
          )}
        </Box>
      )}

      <SaLockerDoorInspector
        open={!!selectedDoor}
        onClose={() => setSelectedDoor(null)}
        doorLabel={
          (selectedDoor as Sst2Door | null)?.door_label ??
          String((selectedDoor as SstDoor | null)?.door_number ?? "")
        }
        apiVersion={apiVersion}
        door={selectedDoor}
        inspection={inspection}
        pairing={pairing}
        provenance={provenance}
        // LOCKER-343 — the partner's v1 locker id, which the per-door repair
        // endpoints key on. Withheld for v2 so the v1-only panel cannot render
        // there even if the version check downstream were ever relaxed.
        sstLockerId={isV2 ? undefined : locker.sstLockerId}
      />

      <SaLockerObjectViewer
        open={objectViewerOpen}
        onClose={() => setObjectViewerOpen(false)}
        locker={locker as unknown as Record<string, unknown>}
      />
    </Box>
  )
}

export default SaLockerDashboardView
